import { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Space, Popconfirm, message, Tooltip, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined
} from '@ant-design/icons';
import { api } from '../api';
import InvoiceForm from './InvoiceForm';

const STATUS_COLORS = {
  draft: 'default',
  sent: 'processing',
  paid: 'success',
  cancelled: 'error',
};
const STATUS_LABELS = {
  draft: 'Entwurf',
  sent: 'Gesendet',
  paid: 'Bezahlt',
  cancelled: 'Storniert',
};

function fmt(n) {
  return Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function InvoicesTab({ invoiceType }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInvoices(await api.invoices.list(invoiceType));
    } catch (e) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [invoiceType]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditingInvoice(null);
    setFormOpen(true);
  };

  const openEdit = async (record) => {
    try {
      const full = await api.invoices.get(record.id);
      setEditingInvoice(full);
      setFormOpen(true);
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleCopy = async (id) => {
    try {
      await api.invoices.copy(id);
      message.success('Rechnung kopiert');
      load();
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.invoices.delete(id);
      message.success('Rechnung gelöscht');
      load();
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleSave = () => {
    setFormOpen(false);
    load();
  };

  const columns = [
    {
      title: 'Nummer', dataIndex: 'invoice_number', key: 'invoice_number',
      sorter: (a, b) => a.invoice_number.localeCompare(b.invoice_number),
    },
    {
      title: invoiceType === 'incoming' ? 'Lieferant' : 'Kunde',
      dataIndex: 'contact_name', key: 'contact_name',
      sorter: (a, b) => a.contact_name.localeCompare(b.contact_name),
    },
    {
      title: 'Datum', dataIndex: 'invoice_date', key: 'invoice_date',
      render: (d) => d ? new Date(d).toLocaleDateString('de-DE') : '',
      sorter: (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Netto (€)', dataIndex: 'net_total', key: 'net_total',
      align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</span>,
      sorter: (a, b) => a.net_total - b.net_total,
    },
    {
      title: 'MwSt.', dataIndex: 'tax_rates', key: 'tax_rates',
      align: 'right',
      render: (v) => v ? `${v} %` : <span style={{ color: '#bbb' }}>–</span>,
    },
    {
      title: 'Brutto (€)', dataIndex: 'gross_total', key: 'gross_total',
      align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(v)}</span>,
      sorter: (a, b) => a.gross_total - b.gross_total,
    },
    {
      title: 'Bezahlt am', dataIndex: 'payment_date', key: 'payment_date',
      render: (d) => d ? new Date(d).toLocaleDateString('de-DE') : <span style={{ color: '#bbb' }}>–</span>,
    },
    {
      title: 'Kategorie', dataIndex: 'category', key: 'category',
      render: (c) => c || <span style={{ color: '#bbb' }}>–</span>,
      filters: [
        'Arbeitsmittel','Bewirtung','Fortbildung','Fremdleistungen',
        'Geringwertige Wirtschaftsgüter','Geschenke','Laufende EDV-Kosten',
        'Sonstige','Sonstige tatsächliche Fahrtkosten','Telekommunikation',
        'Verpackung und Transport','Wareneinkauf','Werbekosten',
      ].map(c => ({ text: c, value: c })),
      onFilter: (v, r) => r.category === v,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s) => <Badge status={STATUS_COLORS[s]} text={STATUS_LABELS[s]} />,
      filters: Object.entries(STATUS_LABELS).map(([v, t]) => ({ text: t, value: v })),
      onFilter: (v, r) => r.status === v,
    },
    {
      title: 'Aktionen', key: 'actions', width: 130,
      render: (_, record) => (
        <Space>
          <Tooltip title="Bearbeiten">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="Kopieren">
            <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Rechnung wirklich löschen?"
            onConfirm={() => handleDelete(record.id)}
            okText="Ja"
            cancelText="Nein"
          >
            <Tooltip title="Löschen">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
          {invoiceType === 'incoming' ? 'Neue Eingangsrechnung' : 'Neue Ausgangsrechnung'}
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={invoices}
        loading={loading}
        pagination={false}
        size="middle"
        bordered
        summary={(data) => {
          if (!data.length) return null;
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>Gesamt: {data.length} Rechnungen</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} colSpan={2} />
            </Table.Summary.Row>
          );
        }}
      />

      <InvoiceForm
        open={formOpen}
        invoiceType={invoiceType}
        invoice={editingInvoice}
        onSave={handleSave}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
